import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  CheckCircle2, XCircle, Loader2, ExternalLink, Printer, FileText, Sparkles, Calendar, Phone,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import { getPublicDetailingQuote, approvePublicDetailingQuote, rejectPublicDetailingQuote } from '../lib/api';

const CATALOG_URL = 'https://drive.google.com/file/d/1iMGIYAQG1GuqMP3VYdij0AbJxfgzCLU_/view?usp=drivesdk';

const STATUS_META = {
  borrador: { label: 'Borrador', color: 'bg-slate-800/60 text-slate-200 border-slate-700' },
  enviada: { label: 'Enviada', color: 'bg-blue-500/20 text-blue-200 border-blue-500/40' },
  aceptada: { label: 'Aceptada', color: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40' },
  rechazada: { label: 'Rechazada', color: 'bg-red-500/20 text-red-200 border-red-500/40' },
  convertida: { label: 'Convertida en orden', color: 'bg-violet-500/20 text-violet-200 border-violet-500/40' },
};

export default function PublicQuotePage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    getPublicDetailingQuote(token)
      .then((r) => setQuote(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Cotización no encontrada'))
      .finally(() => setLoading(false));
  }, [token]);

  const refresh = async () => {
    const r = await getPublicDetailingQuote(token);
    setQuote(r.data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
      </div>
    );
  }
  if (!quote) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center text-center px-4">
        <div>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Cotización no encontrada</h1>
          <p className="text-slate-400">El enlace pudo haber caducado o no ser válido. Contacta a WD Wrap &amp; Detailing Studio.</p>
        </div>
      </div>
    );
  }

  const status = quote.status;
  const isPending = status === 'borrador' || status === 'enviada';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Logo header */}
        <div className="text-center pb-4">
          <img
            src="/wd-detailing-logo.jpg"
            alt="WD Wrap & Detailing Studio"
            className="mx-auto w-40 h-40 object-contain rounded-2xl shadow-2xl shadow-blue-500/20"
            data-testid="public-quote-logo"
          />
          <p className="text-xs uppercase tracking-widest text-blue-300 mt-3">Cotización premium</p>
        </div>

        {/* Card */}
        <div className="bg-slate-900/70 backdrop-blur border border-slate-800 rounded-2xl p-6 md:p-8 shadow-2xl">
          <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-slate-800">
            <div>
              <div className="font-mono text-blue-400 text-lg" data-testid="public-quote-folio">{quote.folio}</div>
              <p className="text-xs text-slate-400 mt-1">Emitida el {new Date(quote.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <span className={`text-xs px-3 py-1.5 rounded-full border font-medium ${STATUS_META[status]?.color}`} data-testid="public-quote-status">
              {STATUS_META[status]?.label || status}
            </span>
          </div>

          {/* Client */}
          <div className="py-4 border-b border-slate-800">
            <p className="text-xs uppercase text-slate-500 mb-1">Cliente</p>
            <p className="text-lg font-semibold">{quote.client?.name || '—'}</p>
            {quote.client?.phone && <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" />{quote.client.phone}</p>}
            {quote.client?.vehicle_info && <p className="text-sm text-slate-400 mt-1.5">{quote.client.vehicle_info}</p>}
          </div>

          {/* Items */}
          <div className="py-4 border-b border-slate-800">
            <p className="text-xs uppercase text-slate-500 mb-3">Servicios cotizados</p>
            <ul className="space-y-3">
              {quote.items.map((it, i) => (
                <li key={i} className="flex justify-between items-start gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <Sparkles className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                    <span className="text-slate-200">{it.description}</span>
                  </div>
                  <span className="font-mono font-semibold text-blue-300 whitespace-nowrap">{formatCurrency(it.price)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Total */}
          <div className="py-5 flex justify-between items-baseline">
            <span className="text-xl font-bold">Total</span>
            <span className="text-4xl font-bold text-blue-400 tabular-nums" data-testid="public-quote-total">{formatCurrency(quote.total)}</span>
          </div>

          {quote.notes && (
            <div className="pt-4 border-t border-slate-800">
              <p className="text-xs uppercase text-slate-500 mb-1">Notas</p>
              <p className="text-sm text-slate-300 italic">{quote.notes}</p>
            </div>
          )}

          <div className="pt-4 text-xs text-slate-500 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Válida por {quote.validity_days} días a partir de la fecha de emisión.
          </div>

          {quote.status === 'aceptada' && quote.approved_by_client && (
            <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg" data-testid="public-quote-approved">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 inline mr-2" />
              <span className="text-emerald-300 font-medium">Aprobada por {quote.approved_by_client}</span>
              {quote.approved_at && <span className="text-emerald-500 text-xs ml-2">· {new Date(quote.approved_at).toLocaleString('es-MX')}</span>}
            </div>
          )}
          {quote.status === 'rechazada' && (
            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/40 rounded-lg" data-testid="public-quote-rejected">
              <XCircle className="w-5 h-5 text-red-400 inline mr-2" />
              <span className="text-red-300 font-medium">Cotización rechazada</span>
              {quote.rejected_reason && <p className="text-sm text-red-300/80 mt-1">Motivo: {quote.rejected_reason}</p>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={CATALOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-800 border border-slate-700 hover:bg-slate-700 text-slate-100 font-medium transition-colors"
            data-testid="public-catalog-btn"
          >
            <FileText className="w-4 h-4" /> Ver catálogo de servicios
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-800 border border-slate-700 hover:bg-slate-700 text-slate-100 font-medium transition-colors"
            data-testid="public-print-btn"
          >
            <Printer className="w-4 h-4" /> Imprimir / PDF
          </button>
        </div>

        {isPending && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => setApproveOpen(true)}
              className="h-14 bg-emerald-600 hover:bg-emerald-500 text-lg font-bold gap-2"
              data-testid="public-approve-btn"
            >
              <CheckCircle2 className="w-5 h-5" /> Aprobar cotización
            </Button>
            <Button
              onClick={() => setRejectOpen(true)}
              variant="outline"
              className="h-14 bg-transparent border-red-500/60 text-red-300 hover:bg-red-500/10 hover:text-red-200 text-lg font-medium gap-2"
              data-testid="public-reject-btn"
            >
              <XCircle className="w-5 h-5" /> Rechazar
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-slate-500 pt-6">
          © WD Wrap &amp; Detailing Studio · Documento generado electrónicamente
        </p>
      </div>

      <ApproveModal open={approveOpen} onOpenChange={setApproveOpen} token={token} onDone={refresh} defaultName={quote.client?.name || ''} />
      <RejectModal open={rejectOpen} onOpenChange={setRejectOpen} token={token} onDone={refresh} />
    </div>
  );
}

function ApproveModal({ open, onOpenChange, token, onDone, defaultName }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setName(defaultName || ''); }, [open, defaultName]);

  const submit = async () => {
    if (name.trim().length < 2) { toast.error('Escribe tu nombre completo'); return; }
    setBusy(true);
    try {
      await approvePublicDetailingQuote(token, name.trim());
      toast.success('¡Cotización aprobada! Nos pondremos en contacto contigo.');
      onOpenChange(false);
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo aprobar');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="approve-quote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Aprobar cotización</DialogTitle>
          <DialogDescription>Confirma con tu nombre para dejar constancia de la aprobación.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <label className="text-sm font-medium">Tu nombre completo</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="approve-name-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="approve-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Confirmar aprobación</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectModal({ open, onOpenChange, token, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await rejectPublicDetailingQuote(token, reason.trim() || null);
      toast.success('Cotización rechazada. Gracias por avisarnos.');
      onOpenChange(false);
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reject-quote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-600" /> Rechazar cotización</DialogTitle>
          <DialogDescription>¿Nos compartes brevemente el motivo? Nos ayuda a mejorar.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} placeholder="Opcional" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} variant="destructive" className="gap-2" data-testid="reject-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar rechazo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
